import React from 'react';
import PropTypes from 'prop-types';
import ValidationErrorMessage from './ValidationErrorMessage';
import { useFormContext } from 'react-hook-form';
import { createRegistrationSettings } from '../utils/utils';

/**
 * Component that returns a select list.
 *
 * @params see propTypes
 * @returns {JSX.Element}
 * @component
 */
const SelectList = ({
  name,
  label,
  placeholder,
  options,
  optionValueKey,
  optionTextKey,
  defaultValue,
  validation,
  customValidation,
  displayError
}) => {
  /**
   * Deconstructs react hook form methods from parent form.
   */
  const {
    register,
    formState: { errors }
  } = useFormContext();

  /**
   * create Settings used in react hook form register function.
   * @type {{}}
   */
  const settings = createRegistrationSettings(label, defaultValue, validation, customValidation);

  /**
   * Takes an array of objects and normalises to a specific structure used to generate select list options.
   *
   * @param {array} options - Array of objects
   * @param {string} valueKey- The key of the option value
   * @param {string} textKey - The key of the option text
   * @returns {array} - The normalised array.
   */
  const normaliseOptions = (options, valueKey, textKey) => {
    return options.map((option) => {
      return {
        value: option[valueKey],
        text: option[textKey]
      };
    });
  };

  return (
    <>
      <label
        htmlFor={name}
        className="mt-8 fw-normal">
        {label} {validation?.IsRequired || customValidation?.required ? '' : '(optional)'}
      </label>

      {
        displayError && errors[name] &&
        <div className="mt-2">
          <ValidationErrorMessage error={errors[name]}/>
        </div>
      }

      <div className="c-select-wrapper position-relative mt-2">
        <select
          id={name}
          className={`border-1 border-${displayError && errors[name] ? 'rhubarb-triangle' : 'blue-anchor'} py-3 ps-2 pe-12 w-100 text-black`}
          {...register(name, settings)}>
          <option value="">
            {placeholder ?? 'Select...'}
          </option>

          {
            normaliseOptions(options, optionValueKey, optionTextKey).map(option =>
              <option key={option.value}
                value={option.value}>
                {option.text}
              </option>
            )
          }
        </select>
      </div>
    </>
  );
};

SelectList.propTypes = {
  /**
   * The input name
   */
  name: PropTypes.string.isRequired,
  /**
   * The label text
   */
  label: PropTypes.string.isRequired,
  /**
   * An options array to generate the select list options.
   */
  options: PropTypes.array.isRequired,
  /**
   * The key in the options array of objects that represents the option value
   */
  optionValueKey: PropTypes.string.isRequired,
  /**
   * The key in the options array of objects that represents the option text
   */
  optionTextKey: PropTypes.string.isRequired,
  /**
   * A default value
   */
  defaultValue: PropTypes.any,
  /**
   * A validation object provided by the settings api compatible with react hook form.
   */
  validation: PropTypes.object,
  /**
   * A validation object declared on the component compatible with react hook form.
   */
  customValidation: PropTypes.object,
  /**
   * Display validation error component.
   */
  displayError: PropTypes.bool,
  /**
   * String value for first option in select element.
   */
  placeholder: PropTypes.string
};

SelectList.defaultProps = {
  displayError: true
};

export default SelectList;
